import type { RectModel } from '@/types/documents.types';

/** A rectangle in CSS pixels, ready to be written into `style` as an absolutely positioned box. */
export interface PixelRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Scale one finding rectangle from PDF points into CSS pixels.
 *
 * The backend returns rectangles in the displayed page's own coordinate system with the origin in
 * the top-left corner (ADR 0006), which is exactly the orientation a CSS overlay uses. So the only
 * transform needed is the ratio between the width the page is rendered at and the width the
 * backend measured it in.
 */
export const scaleRect = (rect: RectModel, factor: number): PixelRect => ({
  left: rect.x0 * factor,
  top: rect.y0 * factor,
  width: Math.max(rect.x1 - rect.x0, 0) * factor,
  height: Math.max(rect.y1 - rect.y0, 0) * factor,
});

/**
 * The scale at which a page of `pageWidth` points fills `containerWidth` pixels.
 *
 * Returns 0 for a container that has not been measured yet, so a first render before the
 * `ResizeObserver` fires draws nothing rather than a page of the wrong size.
 */
export const fitScale = (containerWidth: number, pageWidth: number): number => {
  if (containerWidth <= 0 || pageWidth <= 0) return 0;
  return containerWidth / pageWidth;
};

/** Keep a value inside `[min, max]`; used for zoom steps and for clamping a popover to the page. */
export const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

/** True when the two half-open intervals `[aStart, aEnd)` and `[bStart, bEnd)` share a character. */
export const spansOverlap = (aStart: number, aEnd: number, bStart: number, bEnd: number): boolean =>
  aStart < bEnd && bStart < aEnd;
